import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingState } from "../../components/states";
import { SettingPanel, SettingSwitch } from "../../components/admin/SettingPanel";
import panel from "../../components/admin/AdminPanel.module.css";
import { useToast } from "../../components/toast/toastContext";
import { fetchCapexBudgetSetting, updateCapexBudgetSetting } from "../../api/capexBudgetSetting";

const SettingsWritePermission = "settings.write";

/**
 * The system setting for whether spend by a Capex unit counts against that unit's budget. Capex spend is reported separately either way;
 * this decides only whether it is set against the budget. A change is saved as soon as the switch is flipped and confirmed with a
 * toast; the switch goes back if the save is refused.
 */
export function CapexBudgetSection(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded" } | { kind: "error"; message: string }>({ kind: "loading" });
  const [counts, setCounts] = useState(true);
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchCapexBudgetSetting();
    if (result.kind === "ok") {
      setCounts(result.response.countsAgainstBudget);
      setState({ kind: "loaded" });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.capexBudget.denied") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = async (next: boolean) => {
    setPending(true);
    const result = await updateCapexBudgetSetting(next);
    setPending(false);
    if (result.kind === "ok") {
      setCounts(result.response.countsAgainstBudget);
      toast.show(t("admin.capexBudget.saved"), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  return (
    <SettingPanel
      id="capex-budget"
      title={t("admin.capexBudget.title")}
      intro={t("admin.capexBudget.explanation")}
      headerActions={
        state.kind === "loaded" ? <SettingSwitch label={t("admin.capexBudget.toggleLabel")} checked={counts} onChange={(next) => void change(next)} disabled={pending} /> : undefined
      }
    >
      {state.kind === "loading" && <LoadingState label={t("admin.capexBudget.loading")} />}
      {state.kind === "error" && <ErrorState description={state.message} onRetry={() => void load()} />}
      {state.kind === "loaded" && (
        <p className={panel.footnotes} role="status">
          {counts ? t("admin.capexBudget.countsOn") : t("admin.capexBudget.countsOff")}
        </p>
      )}
    </SettingPanel>
  );
}
